import React from 'react';
import { useArrowStore } from '@/stores/arrowStore';
import { 
  ArrowRight, 
  ArrowLeft, 
  ArrowLeftRight, 
  Minus,
  ArrowRightLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ArrowStyleButtonProps {
  icon: React.ElementType;
  label: string;
  isActive?: boolean;
  onClick: () => void;
}

function ArrowStyleButton({ icon: Icon, label, isActive, onClick }: ArrowStyleButtonProps) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200',
            'hover:bg-sidebar-accent/60 hover:scale-110',
            'active:scale-95',
            isActive 
              ? 'bg-primary/20 text-primary ring-1 ring-primary/30' 
              : 'text-sidebar-foreground'
          )}
        >
          <Icon className="h-5 w-5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function ArrowFormattingSidebar() {
  const { selectedArrowId, arrows, updateArrow } = useArrowStore();
  
  const selectedArrow = arrows.find(a => a.id === selectedArrowId);
  
  if (!selectedArrow) return null;
  
  const handleToggleStartArrow = () => {
    updateArrow(selectedArrow.id, { 
      start_arrowhead: !selectedArrow.start_arrowhead 
    });
  };
  
  const handleToggleEndArrow = () => {
    updateArrow(selectedArrow.id, { 
      end_arrowhead: !selectedArrow.end_arrowhead 
    });
  };
  
  const handleSwapArrows = () => {
    updateArrow(selectedArrow.id, { 
      start_arrowhead: selectedArrow.end_arrowhead,
      end_arrowhead: selectedArrow.start_arrowhead,
    });
  };
  
  const handleNoArrows = () => {
    updateArrow(selectedArrow.id, { 
      start_arrowhead: false,
      end_arrowhead: false,
    });
  };
  
  const handleBothArrows = () => {
    updateArrow(selectedArrow.id, { 
      start_arrowhead: true,
      end_arrowhead: true,
    });
  };
  
  // Determine current state for highlighting
  const hasStartOnly = selectedArrow.start_arrowhead && !selectedArrow.end_arrowhead;
  const hasEndOnly = !selectedArrow.start_arrowhead && selectedArrow.end_arrowhead;
  const hasBoth = selectedArrow.start_arrowhead && selectedArrow.end_arrowhead;
  const hasNone = !selectedArrow.start_arrowhead && !selectedArrow.end_arrowhead;

  return (
    <div className="flex flex-col items-center gap-1 px-3 animate-fade-in">
      {/* Arrowhead configuration */}
      <div className="flex flex-col items-center gap-1">
        <ArrowStyleButton
          icon={ArrowRight}
          label="Arrow at end"
          isActive={hasEndOnly}
          onClick={() => updateArrow(selectedArrow.id, { start_arrowhead: false, end_arrowhead: true })}
        />
        <ArrowStyleButton
          icon={ArrowLeft}
          label="Arrow at start"
          isActive={hasStartOnly}
          onClick={() => updateArrow(selectedArrow.id, { start_arrowhead: true, end_arrowhead: false })}
        />
        <ArrowStyleButton
          icon={ArrowLeftRight}
          label="Arrows on both ends"
          isActive={hasBoth}
          onClick={handleBothArrows}
        />
        <ArrowStyleButton
          icon={Minus}
          label="No arrows (line only)"
          isActive={hasNone}
          onClick={handleNoArrows}
        />
      </div>

      {/* Divider */}
      <div className="w-6 h-px bg-border/60 my-2" />

      {/* Swap */}
      <div className="flex flex-col items-center gap-1">
        <ArrowStyleButton
          icon={ArrowRightLeft}
          label="Swap arrow direction"
          onClick={handleSwapArrows}
        />
      </div>
    </div>
  );
}
